import type { TFunction } from 'i18next';

/**
 * Mapeia os labels recebidos para chaves do i18n.
 * Se houver um mapeamento explícito, retorna a tradução.
 * Caso contrário, tenta normalizar a string ou retorna o original.
 */
const LABEL_KEY_MAP: Record<string, string> = {
  'Arquivos': 'files',
  'Autores': 'authors',
  'Canais': 'channels',
  'Checado': 'checked',
  'Classificado por': 'classifiedBy',
  'Comentários': 'comments',
  'Compartilhamentos': 'shares',
  'Contendo texto': 'containingText',
  'Criticidade': 'criticality',
  'Curtidas/Gostei': 'likes',
  'Gênero': 'gender',
  'Grupos': 'groups',
  'Idioma Detectado': 'languageDetected',
  'Interesses': 'interests',
  'Intervalo de datas': 'period',
  'Língua': 'language',
  'Localidades': 'locations',
  'Páginas': 'pages',
  'Publicações anotadas': 'annotatedPosts',
  'Publicações com transcrição imagem via STILINGUE Eyes': 'collectedByAI',
  'Respondente': 'respondent',
  'Roteamentos': 'routings',
  'Seguidores/Inscritos': 'followers',
  'Sentimento': 'sentiment',
  'Somente publicações coletadas via STILINGUE Eyes': 'collectedByEyesOnly',
  'Tags': 'tags',
  'Temas': 'themes',
  'Tipo de publicação': 'postType',
  'Tipos de interação': 'interactionTypes',
  'Tipos de publicações': 'postTypes',
  'Usuários': 'users',
  'Views': 'views',
};


export interface ParsedLabel {
  label: string;
  value: string;
  originalLabel: string;
  allValues: string[];
  isTruncated: boolean;
  hiddenValues: string[];
  overflowCount: number;
}

/**
 * Faz o parse de uma string do tipo "<b>Label</b>: Valor"
 * Retorna o label traduzido (se disponível), o valor processado e a lista completa de valores.
 */
export const parseHtmlLabel = (htmlString: string, t: TFunction): ParsedLabel => {
  const regex = /<b>(.*?)<\/b>:\s*(.*)/;
  const match = regex.exec(htmlString);
  
  if (!match) {
    return { label: '', value: htmlString, originalLabel: '', allValues: [], isTruncated: false, hiddenValues: [], overflowCount: 0 };
  }

  const [, rawLabel, value] = match;

  const values = value.split(',').map(v => v.trim()).filter(Boolean);
  
  const MAX_VALUES = 3;
  const isTruncated = values.length > MAX_VALUES;
  const hiddenValues = isTruncated ? values.slice(MAX_VALUES) : [];
  const overflowCount = hiddenValues.length;

  // Visible value excludes the (+n) indicator — that is rendered as a separate element.
  const processedValue = isTruncated
    ? values.slice(0, MAX_VALUES).join(', ')
    : value;

  const i18nKey = LABEL_KEY_MAP[rawLabel];
  const translatedLabel = i18nKey ? t(`filterBar.${i18nKey}`) : rawLabel;

  return {
    label: translatedLabel,
    value: processedValue,
    originalLabel: rawLabel,
    allValues: values,
    isTruncated,
    hiddenValues,
    overflowCount,
  };
};
